import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
import { motion } from "framer-motion";
import useLabels from "hooks/useLabels";
import { X } from "lucide-react";
import { throttle } from "lodash";
import { Fragment, useEffect, useRef, useState } from "react";

const RESIZE_THROTTLE_MS = 16;
const MAX_DRAGGABLE_SIDEBAR_WIDTH = 0.6; // Must be a percentage < 1

export type DrawerContentProps = {
  /**
   * The content of the drawer.
   */
  children?: React.ReactNode;
  /**
   * The function to close the drawer.
   */
  onClose: (value: boolean) => void;
  /**
   * The header content of the drawer.
   */
  headerContent: {
    /**
     * The title of the drawer.
     */
    title: React.ReactNode;
    /**
     * The subtitle of the drawer.
     */
    subtitle?: React.ReactNode;
    /**
     * The action bar of the drawer. An additional bar that is not included in the scroll area.
     */
    actionBar?: React.ReactNode;
  };
  /**
   * The footer of the drawer.
   */
  footer?: React.ReactNode;
  /**
   * Whether the scroll bar is hidden. When hidden, users can still scroll the drawer.
   */
  hideScrollBar?: boolean;
  /**
   * Whether the drawer is full height when on a mobile device.
   */
  fullHeight?: boolean;
  /**
   * The size of the drawer controlling the width on desktop.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Whether to disable the ScrollArea wrapper. Useful for components that handle their own scrolling.
   */
  disableScrollArea?: boolean;
};

export type DialogDrawerProps = DrawerContentProps & {
  /**
   * Whether the drawer is open.
   */
  open?: boolean;
  /**
   * Whether the drawer has an overlay.
   */
  overlay?: boolean;
};

export type EmbeddedDrawerProps = DrawerContentProps & {
  /**
   * Whether the drawer is open (for animation purposes).
   */
  open?: boolean;
  /**
   * Whether the drawer can be resized by dragging its left edge.
   */
  resizable?: boolean;
  /**
   * Minimum width when resizing.
   */
  minWidth?: "sm" | "md" | "lg";
  /**
   * Initial width when open, overrides size default if provided.
   */
  initialWidth?: number;
};

const getDrawerMaxWidthClasses = (size: "sm" | "md" | "lg") => {
  const maxWidthMap = {
    sm: "sm:max-w-xs",
    md: "sm:max-w-md",
    lg: "sm:max-w-[500px]",
  };
  return maxWidthMap[size];
};

// Width values for different drawer sizes
const getDrawerWidth = (size: "sm" | "md" | "lg") => {
  const widthMap = {
    sm: 320, // max-w-xs
    md: 448, // max-w-md
    lg: 500, // max-w-[500px]
  };
  return widthMap[size];
};

/**
 * The core drawer content component that contains the header, content, and footer.
 */
export function DrawerContent({
  children,
  onClose,
  headerContent,
  footer,
  hideScrollBar = false,
  fullHeight = false,
  disableScrollArea = false,
}: DrawerContentProps) {
  return (
    <div
      data-testid="drawer"
      className="flex flex-col h-full bg-bg-base shadow-xl rounded-t-[20px] sm:rounded-none"
    >
      {/* Header and optional action bar */}
      <DrawerHeader headerContent={headerContent} onClose={onClose} />
      {/* Content */}
      {disableScrollArea ? (
        <div
          className="flex overflow-y-auto relative flex-col flex-1"
          data-testid="drawer-content"
        >
          {children}
        </div>
      ) : (
        <ScrollArea
          className="flex overflow-y-auto relative flex-col flex-1"
          hideScrollBar={hideScrollBar}
        >
          {children}
        </ScrollArea>
      )}
      {/* Footer */}
      <DrawerFooter footer={footer} onClose={onClose} />
    </div>
  );
}

/**
 * A drawer that renders as a modal dialog with overlay and animations.
 */
export function DialogDrawer({
  open,
  onClose,
  overlay,
  size = "sm",
  ...drawerContentProps
}: DialogDrawerProps) {
  const panelClassName = classNames(
    "relative w-screen pointer-events-auto max-w-screen",
    getDrawerMaxWidthClasses(size)
  );

  const content = (
    <>
      <Transition.Child
        as={Fragment}
        enter="ease-in-out duration-500"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in-out duration-500"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className={classNames("fixed inset-0 transition-opacity", {
            "bg-[var(--color-backdrop-background)]": overlay,
            "bg-[var(--color-backdrop-background)] sm:bg-transparent": !overlay,
          })}
        />
      </Transition.Child>

      <div className="overflow-hidden fixed inset-0">
        <div className="overflow-hidden absolute inset-0">
          <div
            className={classNames(
              "flex pointer-events-none fixed sm:inset-y-0 bottom-0 right-0",
              {
                "h-[calc(100dvh-40px)] sm:h-full max-w-full":
                  drawerContentProps.fullHeight,
                "max-h-[calc(100dvh-40px)] sm:max-h-full max-w-full":
                  !drawerContentProps.fullHeight,
              }
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-200 sm:duration-400"
              enterFrom="translate-y-full sm:translate-x-full sm:translate-y-0"
              enterTo="translate-y-0 sm:translate-x-0 sm:translate-y-0"
              leave="transform transition ease-in-out duration-200 sm:duration-400"
              leaveFrom="translate-y-0 sm:translate-x-0 sm:translate-y-0"
              leaveTo="translate-y-full sm:translate-x-full sm:translate-y-0"
            >
              <Dialog.Panel className={panelClassName}>
                <DrawerContent onClose={onClose} {...drawerContentProps} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {content}
      </Dialog>
    </Transition.Root>
  );
}

/**
 * A drawer that renders as a simple div without animations, suitable for embedding.
 * The caller is responsible for controlling animations and positioning.
 */
export function EmbeddedDrawer({
  open = true,
  size = "sm",
  resizable = false,
  minWidth = "sm",
  initialWidth,
  ...drawerContentProps
}: EmbeddedDrawerProps) {
  const defaultWidth = initialWidth ?? getDrawerWidth(size);
  const [width, setWidth] = useState<number>(defaultWidth);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Object ref to store all drag state
  const dragState = useRef<{
    isActive: boolean;
    startX: number;
    startWidth: number;
    pointerId: number | null;
  }>({
    isActive: false,
    startX: 0,
    startWidth: defaultWidth,
    pointerId: null,
  });

  const resizeHandleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      // Do not animate width changes while closed
      return;
    }
    // Intentionally do not reset width on size/open changes to preserve user resizing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultWidth]);

  const clampWidth = (value: number): number => {
    const min = Math.max(getDrawerWidth("sm"), getDrawerWidth(minWidth)); // Minimum is "sm" size
    const max = Math.max(
      min + 1,
      window.innerWidth * Math.min(MAX_DRAGGABLE_SIDEBAR_WIDTH, 1)
    ); // Calculate max based on screen width
    return Math.min(Math.max(value, min), max);
  };

  const handlePointerMove = throttle((event: PointerEvent) => {
    if (!dragState.current.isActive) return;
    const deltaX = dragState.current.startX - event.clientX;
    const nextWidth = clampWidth(dragState.current.startWidth + deltaX);
    setWidth(nextWidth);
  }, RESIZE_THROTTLE_MS);

  const endDrag = () => {
    if (!dragState.current.isActive) return;

    dragState.current.isActive = false;
    setIsDragging(false);

    // Release pointer capture
    if (dragState.current.pointerId !== null && resizeHandleRef.current) {
      try {
        resizeHandleRef.current.releasePointerCapture(
          dragState.current.pointerId
        );
      } catch {
        // Ignore - pointer was likely already released by browser
      }
    }

    dragState.current.pointerId = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  const onPointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!resizable || !event.isPrimary) return;

    event.preventDefault();
    event.stopPropagation();

    dragState.current = {
      isActive: true,
      startX: event.clientX,
      startWidth: width ?? defaultWidth,
      pointerId: event.pointerId,
    };

    setIsDragging(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    // Use pointer capture for all events on this element
    try {
      resizeHandleRef.current?.setPointerCapture(event.pointerId);
    } catch {
      endDrag();
    }
  };

  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    handlePointerMove(event.nativeEvent);
  };

  const onPointerUp: React.PointerEventHandler<HTMLDivElement> = () => {
    endDrag();
  };

  const onPointerCancel: React.PointerEventHandler<HTMLDivElement> = () => {
    endDrag();
  };

  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  return (
    <motion.div
      style={{ width }}
      className={classNames("relative h-full", {
        "border-l border-border-base": open,
      })}
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: open ? width : 0, opacity: open ? 1 : 0 }}
      transition={{
        duration: isDragging ? 0 : 0.3,
        ease: "easeInOut",
      }}
    >
      {resizable && open && (
        <div
          ref={resizeHandleRef}
          aria-label="Resize sidebar"
          role="separator"
          aria-orientation="vertical"
          className="absolute left-[-6px] top-0 h-full cursor-col-resize z-10 touch-none w-3 hover:bg-border-base/50"
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        />
      )}
      <div className="overflow-hidden w-full h-full">
        <DrawerContent {...drawerContentProps} />
      </div>
    </motion.div>
  );
}

type DrawerHeaderProps = Pick<DrawerContentProps, "headerContent" | "onClose">;

const DrawerHeader = ({ headerContent, onClose }: DrawerHeaderProps) => {
  return (
    <div className="box-content sticky top-0 -ml-px w-full rounded-t-xl border bg-bg-base sm:box-border border-border-base sm:border-t-0 sm:border-x-0 sm:rounded-t-none min-h-header sm:ml-0">
      <div className="flex justify-between items-center -mt-px w-full min-h-header">
        <div className="flex items-center justify-between w-full pl-4 pr-[6px] sm:py-0 py-3">
          <div className="flex flex-col my-[10.5px] w-full">
            <div className="flex justify-between items-center w-full">
              <h2
                className={classNames("lg-bold text-fg-base", {
                  "sm:base-bold": !!headerContent.subtitle,
                })}
              >
                {headerContent.title}
              </h2>
              {/* Close button for mobile inline with title */}
              <div className="block sm:hidden">
                <CoButton
                  variant={ButtonVariant.Flat}
                  size="sm"
                  onClick={() => onClose(false)}
                  icon={<X className="text-fg-base" size={20} />}
                />
              </div>
            </div>
            {headerContent.subtitle && (
              <span
                data-testid="drawer-subtitle"
                className="sm-normal sm:tiny-normal text-fg-muted leading-[normal] line-clamp-1"
              >
                {headerContent.subtitle}
              </span>
            )}
          </div>
          {/* Close button for desktop inline with title and subtitle */}
          <div className="hidden sm:block">
            <CoButton
              variant={ButtonVariant.Flat}
              size="sm"
              onClick={() => onClose(false)}
              icon={<X className="text-fg-base" size={20} />}
            />
          </div>
        </div>
      </div>
      {headerContent.actionBar && headerContent.actionBar}
    </div>
  );
};

type DrawerFooterProps = Pick<DrawerContentProps, "footer" | "onClose">;

const DrawerFooter = ({ footer, onClose }: DrawerFooterProps) => {
  const [generalLabels] = useLabels("general");

  return (
    <div
      className={classNames(
        "flex sticky bottom-0 justify-between items-center p-4 border-t bg-bg-base border-border-base min-h-header",
        {
          "sm:hidden": !footer,
        }
      )}
    >
      {footer ? (
        footer
      ) : (
        <CoButton
          variant={ButtonVariant.Primary}
          onClick={() => onClose(false)}
          size="sm"
          label={generalLabels.done}
          className="w-full"
        />
      )}
    </div>
  );
};
